import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Hook for draggable panel resizing.
 * @param {number} initialWidth - Starting width in pixels
 * @param {{ min?: number, max?: number }} options
 * @returns {{ width: number, startDrag: (e, direction?: 1|-1) => void }}
 *
 * direction = 1  → dragging RIGHT increases the panel width (left panel)
 * direction = -1 → dragging LEFT increases the panel width (right panel)
 */
export function useResizablePanel(initialWidth, { min = 160, max = 800 } = {}) {
  const [width, setWidth] = useState(initialWidth);
  const state = useRef({ dragging: false, startX: 0, startWidth: 0, direction: 1 });

  const startDrag = useCallback((e, direction = 1) => {
    e.preventDefault();
    state.current = { dragging: true, startX: e.clientX, startWidth: width, direction };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [width]);

  useEffect(() => {
    const onMove = (e) => {
      if (!state.current.dragging) return;
      const delta = (e.clientX - state.current.startX) * state.current.direction;
      setWidth(Math.min(max, Math.max(min, state.current.startWidth + delta)));
    };
    const onUp = () => {
      if (!state.current.dragging) return;
      state.current.dragging = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [min, max]);

  return { width, startDrag };
}
